/**
 * THE SEA'S OPEN EDGES. A room's water normally ends inside the room: past the border is dry, so every body of
 * water gets a shore and a bank all round. A sea runs on past the border instead. A room lists the borders its
 * water runs off in the `waterOpenEdges` map property ("north", or a JSON array such as ["north", "east"]),
 * and past those borders the water reads as whatever is on the border itself. The client then draws no bank
 * there, and depth counts the water beyond.
 */
export type WaterEdge = "north" | "south" | "east" | "west"

/** A water layer's cell values: dry, shallow (next to land), deep (water all round), deeper (the core). */
export const WaterCell = { Dry: 0, Shallow: 1, Deep: 2, Deeper: 3 } as const

export const WATER_OPEN_EDGES_PROPERTY = "waterOpenEdges"

const EDGES: readonly WaterEdge[] = ["north", "south", "east", "west"]

/** The property's value as a list of edges; anything unrecognised is dropped. */
export const parseWaterOpenEdges = (value: unknown): WaterEdge[] => {
    let list: unknown = value
    if (typeof value === "string") {
        const text = value.trim()
        try {
            list = text.startsWith("[") ? JSON.parse(text) : text.split(",")
        } catch {
            list = []
        }
    }
    return Array.isArray(list)
        ? Array.from(new Set(list.map(e => `${e}`.trim().toLowerCase()).filter((e): e is WaterEdge => EDGES.includes(e as WaterEdge))))
        : []
}

/**
 * The cell (x, y) reads its water from: itself inside the room; past an open edge, the nearest cell on that
 * edge; past a closed edge, none (dry).
 */
export const waterSourceCell = (
    x: number, y: number, width: number, height: number, open: readonly WaterEdge[] = [],
): [number, number] | undefined => {
    if (x < 0 && !open.includes("west")) return undefined
    if (x >= width && !open.includes("east")) return undefined
    if (y < 0 && !open.includes("north")) return undefined
    if (y >= height && !open.includes("south")) return undefined
    return [Math.min(width - 1, Math.max(0, x)), Math.min(height - 1, Math.max(0, y))]
}

/** How far round a DEEPER cell, in cells, there is nothing but water. */
export const DEEPER_REACH = 2

/**
 * One WaterCell per cell (row-major) from which cells hold water: SHALLOW where land is next to it, DEEP where
 * water is on all eight sides, DEEPER where water is everywhere within DEEPER_REACH. Past an open edge the
 * water goes on, so a sea is as deep at the room's border as just inside it; past a closed edge it is land,
 * as the camp's pond has always counted it.
 */
export const classifyWaterDepth = (
    isWater: (x: number, y: number) => boolean, width: number, height: number, open: readonly WaterEdge[] = [],
): Uint8Array => {
    const wet = (x: number, y: number): boolean => {
        const at = waterSourceCell(x, y, width, height, open)
        return !!at && isWater(at[0], at[1])
    }
    const cells = new Uint8Array(width * height)
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (!isWater(x, y)) continue
        let deep = true
        for (let dy = -1; dy <= 1 && deep; dy++) for (let dx = -1; dx <= 1 && deep; dx++) if (!wet(x + dx, y + dy)) deep = false
        let deeper = deep
        for (let dy = -DEEPER_REACH; dy <= DEEPER_REACH && deeper; dy++) for (let dx = -DEEPER_REACH; dx <= DEEPER_REACH && deeper; dx++) {
            if (dx * dx + dy * dy <= DEEPER_REACH * DEEPER_REACH && !wet(x + dx, y + dy)) deeper = false
        }
        cells[y * width + x] = deeper ? WaterCell.Deeper : deep ? WaterCell.Deep : WaterCell.Shallow
    }
    return cells
}
